<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <!-- 弹窗标题 -->
          <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑订单' : '新增订单' }}</h3>
              <p class="text-sm text-slate-500 mt-0.5">{{ isEdit ? '修改订单信息' : '向当前方案添加新的订单' }}</p>
            </div>
            <button @click="closeModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 表单内容 -->
          <div class="flex-1 overflow-y-auto min-h-0 p-6">
            <div class="max-w-3xl mx-auto">
              <div class="space-y-5">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">
                      产品 <span class="text-red-500">*</span>
                    </label>
                    <el-select
                      v-model="orderForm.productId"
                      placeholder="请选择产品"
                      clearable
                      :disabled="isExecuting"
                      class="w-full"
                    >
                      <el-option
                        v-for="p in productList"
                        :key="p.id"
                        :label="`${p.name} (${p.code})`"
                        :value="p.id"
                      />
                    </el-select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">
                      订单类型 <span class="text-red-500">*</span>
                    </label>
                    <el-select
                      v-model="orderForm.orderType"
                      placeholder="请选择类型"
                      :disabled="isExecuting"
                      class="w-full"
                      @change="handleOrderTypeChange"
                    >
                      <el-option label="一次性服务" value="ONCE" />
                      <el-option label="分期服务" value="PERIODIC" />
                    </el-select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">
                      落地执行人员 <span class="text-red-500">*</span>
                    </label>
                    <el-select
                      v-model="orderForm.executorId"
                      placeholder="请选择执行人员"
                      filterable
                      remote
                      :remote-method="handleExecutorSearch"
                      :loading="executorLoading"
                      :disabled="isEdit"
                      class="w-full"
                      @visible-change="handleExecutorDropdownVisible"
                    >
                      <el-option
                        v-for="u in executorList"
                        :key="u.id"
                        :label="u.displayName || u.username"
                        :value="u.id"
                      >
                        <div class="flex items-center justify-between">
                          <span>{{ u.displayName || u.username }}</span>
                          <span v-if="u.phone" class="ml-2 text-xs text-slate-400">{{ u.phone }}</span>
                        </div>
                      </el-option>
                      <template v-if="executorPagination.total > executorList.length" #footer>
                        <div class="border-t border-slate-200 py-2 text-center">
                          <button
                            type="button"
                            class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                            :disabled="executorLoading"
                            @click.stop="loadMoreExecutors"
                          >
                            {{ executorLoading ? '加载中...' : `加载更多 (${executorList.length}/${executorPagination.total})` }}
                          </button>
                        </div>
                      </template>
                    </el-select>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">服务开始时间</label>
                    <el-date-picker
                      v-model="orderForm.serviceStart"
                      type="date"
                      format="YYYY年MM月DD日"
                      value-format="YYYY-MM-DD"
                      placeholder="请选择开始时间"
                      class="w-full"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">服务结束时间</label>
                    <el-date-picker
                      v-model="orderForm.serviceEnd"
                      type="date"
                      format="YYYY年MM月DD日"
                      value-format="YYYY-MM-DD"
                      placeholder="请选择结束时间"
                      class="w-full"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-2">
                      订单金额 <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                      <span class="absolute left-3 top-2.5 text-slate-500">¥</span>
                      <input
                        v-model.number="orderForm.amount"
                        type="number"
                        step="0.01"
                        class="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="0.00"
                      >
                    </div>
                  </div>
                  <div v-if="orderForm.orderType === 'PERIODIC'">
                    <label class="block text-sm font-medium text-slate-700 mb-2">
                      分期期数 <span class="text-red-500">*</span>
                    </label>
                    <el-select v-model="orderForm.periodCount" placeholder="请选择期数" class="w-full">
                      <el-option v-for="n in 12" :key="n" :label="`${n}期`" :value="n" />
                    </el-select>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">备注</label>
                  <input
                    v-model="orderForm.remark"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="请输入备注（可选）"
                  >
                </div>

                <!-- 账单信息 -->
                <div class="border-t border-slate-200 pt-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                      <span class="text-sm font-medium text-slate-700">账单明细</span>
                    </div>
                    <div class="flex gap-2">
                      <button @click="addBilling" class="text-slate-600 text-sm hover:text-slate-800 font-medium px-2 py-1 hover:bg-slate-100 rounded">
                        + 手动添加
                      </button>
                      <button v-if="!isEdit" @click="generateBillings" class="text-primary-600 text-sm hover:text-primary-700 font-medium px-2 py-1 hover:bg-primary-50 rounded">
                        <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                        </svg>
                        自动生成
                      </button>
                    </div>
                  </div>

                  <div v-if="sortedBillings.length > 0" class="space-y-2">
                    <!-- 表头 -->
                    <div class="flex items-center gap-3 px-3 pb-2 text-xs text-slate-500">
                      <span class="w-16 text-center">期数</span>
                      <span class="w-40">金额</span>
                      <span class="w-44">付款日期</span>
                      <span class="w-8"></span>
                    </div>
                    <!-- 账单行 -->
                    <div v-for="billing in sortedBillings" :key="billing._originalIndex" :class="['flex items-center gap-3 p-3 rounded-lg', isBillingPaid(billing) ? 'bg-slate-100 opacity-70' : 'bg-slate-50']">
                      <el-input
                        :model-value="billing._sortedPeriodIndex"
                        disabled
                        class="!w-16"
                      />
                      <el-input
                        v-model.number="orderForm.billings[billing._originalIndex].amount"
                        type="number"
                        :disabled="isBillingPaid(billing)"
                        placeholder="0.00"
                        class="!w-40"
                      >
                        <template #prefix>¥</template>
                      </el-input>
                      <el-date-picker
                        v-model="orderForm.billings[billing._originalIndex].dueDate"
                        type="date"
                        format="YYYY-MM-DD"
                        value-format="YYYY-MM-DD"
                        placeholder="到期日"
                        :disabled="isBillingPaid(billing)"
                        class="!w-44"
                      />
                      <template v-if="isBillingPaid(billing)">
                        <span class="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 whitespace-nowrap">{{ getBillingStatusText(billing.status) }}</span>
                      </template>
                      <button v-else @click="removeBilling(billing._originalIndex)" class="p-1 text-red-500 hover:text-red-700 flex-shrink-0">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-else class="text-center py-4 text-slate-500 text-sm bg-slate-50 rounded-lg">
                    暂无账单，可手动添加或自动生成
                  </div>

                  <div class="mt-3 flex items-center justify-end gap-4">
                    <span class="text-sm text-slate-600">账单金额合计: </span>
                    <span :class="['font-medium', isBillingTotalValid ? 'text-green-600' : 'text-red-600']">
                      ¥{{ billingTotal.toLocaleString() }}
                    </span>
                    <span v-if="!isBillingTotalValid" class="text-xs text-red-600">（与订单金额不一致）</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
            <button @click="closeModal" class="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50">
              取消
            </button>
            <button @click="handleSubmit" :disabled="submitting" class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {{ submitting ? '提交中...' : (isEdit ? '保存' : '提交订单') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { addOrderToPlan, editOrder } from '@/api/order'
import { getProductPage } from '@/api/product'
import { getUserList } from '@/api/user'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  isEdit: {
    type: Boolean,
    default: false
  },
  editData: {
    type: Object,
    default: null
  },
  planId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const submitting = ref(false)
const productList = ref([])
const executorList = ref([])
const executorLoading = ref(false)
const executorSearchKeyword = ref('')
const executorPagination = reactive({
  page: 1,
  size: 20,
  total: 0
})

const orderForm = reactive({
  productId: '',
  orderType: 'ONCE',
  periodCount: null,
  serviceStart: '',
  serviceEnd: '',
  amount: 0,
  remark: '',
  executorId: null,
  billings: []
})

// 执行中的订单不允许变更产品和订单类型
const isExecuting = computed(() => props.isEdit && props.editData?.status === 1)

// 计算账单总额
const billingTotal = computed(() => {
  if (!orderForm.billings || orderForm.billings.length === 0) return 0
  return orderForm.billings.reduce((sum, b) => sum + (b.amount || 0), 0)
})

// 按付款日期排序的账单列表（用于展示，自动计算期数）
const sortedBillings = computed(() => {
  if (!orderForm.billings || orderForm.billings.length === 0) return []
  return [...orderForm.billings]
    .map((b, originalIndex) => ({ ...b, _originalIndex: originalIndex }))
    .sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0
      if (!a.dueDate) return 1
      if (!b.dueDate) return -1
      return new Date(a.dueDate) - new Date(b.dueDate)
    })
    .map((b, sortedIndex) => ({ ...b, _sortedPeriodIndex: sortedIndex + 1 }))
})

// 账单金额是否与订单金额一致
const isBillingTotalValid = computed(() => {
  return Math.abs(billingTotal.value - (orderForm.amount || 0)) < 0.01
})

// 判断账单是否已收款（已收/部分收/已核销）
const isBillingPaid = (billing) => {
  return billing.status >= 1
}

const billingStatusTextMap = {
  0: '待收',
  1: '已收',
  2: '部分收',
  3: '已核销'
}
const getBillingStatusText = (status) => billingStatusTextMap[status] || ''

// 加载产品列表
const loadProducts = async () => {
  try {
    const data = await getProductPage({ page: 1, size: 100 })
    productList.value = data.records || []
  } catch (error) {
    console.error('加载产品列表失败', error)
  }
}

// 加载执行人员列表
const loadExecutors = async (keyword = '', isLoadMore = false) => {
  if (executorLoading.value) return

  executorLoading.value = true
  try {
    const params = {
      username: keyword || undefined,
      status: 0,
      page: isLoadMore ? executorPagination.page + 1 : 1,
      size: executorPagination.size
    }
    const data = await getUserList(params)
    const records = data?.records || []
    if (isLoadMore) {
      const existingIds = new Set(executorList.value.map(item => item.id))
      executorList.value = [
        ...executorList.value,
        ...records.filter(item => !existingIds.has(item.id))
      ]
      executorPagination.page = data?.current || executorPagination.page + 1
    } else {
      executorList.value = records
      executorPagination.page = data?.current || 1
    }
    executorPagination.total = data?.total || 0
    // 编辑模式下确保当前执行人保留在列表中，避免被覆盖后选中值丢失
    if (props.isEdit && orderForm.executorId && !executorList.value.some(u => u.id === orderForm.executorId) && props.editData?.executor) {
      executorList.value.unshift(props.editData.executor)
    }
  } catch (error) {
    console.error('加载执行人员列表失败', error)
  } finally {
    executorLoading.value = false
  }
}

let executorSearchTimer = null
const handleExecutorSearch = (val) => {
  executorSearchKeyword.value = val
  if (executorSearchTimer) {
    clearTimeout(executorSearchTimer)
  }
  executorSearchTimer = setTimeout(() => {
    loadExecutors(val, false)
  }, 300)
}

const handleExecutorDropdownVisible = (visible) => {
  if (visible && executorList.value.length === 0) {
    loadExecutors('', false)
  }
}

const loadMoreExecutors = () => {
  loadExecutors(executorSearchKeyword.value, true)
}

const handleOrderTypeChange = () => {
  if (orderForm.orderType === 'ONCE') {
    orderForm.periodCount = null
  } else {
    orderForm.periodCount = 1
  }
  orderForm.billings = []
}

// 自动生成账单
const generateBillings = () => {
  if (!orderForm.amount || orderForm.amount <= 0) {
    ElMessage.warning('请先输入订单金额')
    return
  }

  const periodCount = orderForm.orderType === 'PERIODIC' ? (orderForm.periodCount || 1) : 1
  const amountPerPeriod = orderForm.amount / periodCount

  const startDate = orderForm.serviceStart ? new Date(orderForm.serviceStart) : new Date()
  const bills = []

  for (let i = 1; i <= periodCount; i++) {
    const dueDate = new Date(startDate)
    dueDate.setMonth(dueDate.getMonth() + (i - 1))
    const dueDateStr = dueDate.toISOString().split('T')[0]

    bills.push({
      periodIndex: i,
      amount: Number(amountPerPeriod.toFixed(2)),
      dueDate: dueDateStr
    })
  }

  orderForm.billings = bills
}

const addBilling = () => {
  if (!orderForm.billings) orderForm.billings = []
  const maxPeriod = orderForm.billings.reduce((max, b) => Math.max(max, b.periodIndex || 0), 0)
  orderForm.billings.push({
    periodIndex: maxPeriod + 1,
    amount: 0,
    dueDate: ''
  })
}

const removeBilling = (index) => {
  orderForm.billings.splice(index, 1)
}

// 表单验证
const validateForm = () => {
  if (!orderForm.productId) {
    ElMessage.warning('请选择产品')
    return false
  }
  if (!orderForm.orderType) {
    ElMessage.warning('请选择订单类型')
    return false
  }
  if (!orderForm.executorId) {
    ElMessage.warning('请选择落地执行人员')
    return false
  }
  if (!orderForm.amount || orderForm.amount <= 0) {
    ElMessage.warning('请输入有效的订单金额')
    return false
  }
  if (orderForm.orderType === 'PERIODIC' && !orderForm.periodCount) {
    ElMessage.warning('分期订单请选择分期期数')
    return false
  }
  if (orderForm.orderType === 'PERIODIC' && orderForm.billings?.length !== orderForm.periodCount) {
    ElMessage.warning(`分期订单账单数量应为${orderForm.periodCount}期`)
    return false
  }
  if (orderForm.billings?.some(b => !b.dueDate)) {
    ElMessage.warning('请填写所有账单的付款日期')
    return false
  }
  if (orderForm.billings?.length > 0 && !isBillingTotalValid.value) {
    ElMessage.warning('账单金额合计与订单金额不一致')
    return false
  }
  return true
}

// 根据付款日期排序账单并分配期数
const sortBillingsWithPeriodIndex = (billings) => {
  return [...billings]
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .map((b, index) => ({ ...b, periodIndex: index + 1 }))
}

// 提交
const handleSubmit = async () => {
  if (!validateForm()) return

  submitting.value = true
  try {
    if (props.isEdit) {
      const command = {
        orderId: props.editData?.id,
        productId: orderForm.productId,
        orderType: orderForm.orderType,
        periodCount: orderForm.orderType === 'PERIODIC' ? orderForm.periodCount : undefined,
        serviceStart: orderForm.serviceStart || undefined,
        serviceEnd: orderForm.serviceEnd || undefined,
        amount: orderForm.amount,
        remark: orderForm.remark || undefined,
        billings: sortBillingsWithPeriodIndex(orderForm.billings?.filter(b => b.amount > 0 && !isBillingPaid(b)) || []).map(b => ({
          id: b.id || undefined,
          periodIndex: b.periodIndex,
          amount: b.amount,
          dueDate: b.dueDate,
          remark: b.remark || undefined
        }))
      }
      const result = await editOrder(command)
      emit('success', result)
      closeModal()
    } else {
      // 新建模式
      const command = {
        planId: props.planId,
        order: {
          productId: orderForm.productId,
          orderType: orderForm.orderType,
          periodCount: orderForm.orderType === 'PERIODIC' ? orderForm.periodCount : undefined,
          serviceStart: orderForm.serviceStart || undefined,
          serviceEnd: orderForm.serviceEnd || undefined,
          amount: orderForm.amount,
          remark: orderForm.remark || undefined,
          executorId: orderForm.executorId || undefined,
          billings: sortBillingsWithPeriodIndex(orderForm.billings?.filter(b => b.amount > 0) || [])
        }
      }

      const result = await addOrderToPlan(command)

      if (result.needApproval) {
        ElMessage.success('订单提交成功，已进入审批流程')
      } else {
        ElMessage.success('订单添加成功')
      }
      emit('success')
      closeModal()
    }
  } catch (error) {
    ElMessage.error(error.message || (props.isEdit ? '订单修改失败' : '订单添加失败'))
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  Object.assign(orderForm, {
    productId: '',
    orderType: 'ONCE',
    periodCount: null,
    serviceStart: '',
    serviceEnd: '',
    amount: 0,
    remark: '',
    executorId: null,
    billings: []
  })
}

const fillForm = (data) => {
  if (!data) return
  orderForm.productId = data.productId || ''
  orderForm.orderType = data.orderType || ''
  orderForm.periodCount = data.periodCount || null
  orderForm.serviceStart = data.serviceStart || ''
  orderForm.serviceEnd = data.serviceEnd || ''
  orderForm.amount = data.amount || 0
  orderForm.remark = data.remark || ''
  orderForm.executorId = data.executorId || data.executor?.id || null
  orderForm.billings = data.billings?.map(b => ({
    id: b.id,
    periodIndex: b.periodIndex,
    amount: b.amount,
    dueDate: b.dueDate,
    status: b.status ?? 0
  })) || []

  // 编辑模式下确保执行人在列表中
  if (orderForm.executorId && data.executor) {
    const exists = executorList.value.some(u => u.id === orderForm.executorId)
    if (!exists) {
      executorList.value.unshift(data.executor)
    }
  }
  // 编辑模式下确保产品在列表中
  if (orderForm.productId && data.productNameSnapshot) {
    const exists = productList.value.some(p => p.id === orderForm.productId)
    if (!exists) {
      productList.value.unshift({
        id: data.productId,
        name: data.productNameSnapshot,
        code: data.productCodeSnapshot || ''
      })
    }
  }
}

const closeModal = () => {
  emit('update:visible', false)
}

watch(() => props.visible, (val) => {
  if (val) {
    loadProducts()
    loadExecutors()
    if (props.isEdit && props.editData) {
      fillForm(props.editData)
    } else {
      resetForm()
    }
  }
})
</script>

<style scoped>
/* 账单行输入框统一高度和对齐 */
:deep(.el-input),
:deep(.el-date-editor) {
  --el-input-height: 32px;
  height: 32px !important;
  line-height: 32px;
}
:deep(.el-input .el-input__wrapper),
:deep(.el-date-editor .el-input__wrapper) {
  height: 32px !important;
  padding: 0 11px;
  box-sizing: border-box;
}
:deep(.el-input .el-input__inner),
:deep(.el-date-editor .el-input__inner) {
  height: 30px !important;
  line-height: 30px;
}
:deep(.el-date-editor.el-input__wrapper) {
  height: 32px !important;
  display: inline-flex;
  align-items: center;
}
</style>
